import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthChrome } from '../components/auth/AuthChrome';
import { Banner, Button, Card, TextField } from '../components/primitives';
import { supabase } from '../lib/supabase';
import './PasswordReset.css';

const CONFIRM_PATH = '/password-reset/confirm';

function classifyResetError(message: string | undefined, code: string | undefined): ReactNode {
  const msg = (message ?? '').toLowerCase();
  const c = (code ?? '').toLowerCase();
  if (
    c.includes('rate') ||
    c.includes('over_email_send_rate_limit') ||
    msg.includes('rate') ||
    msg.includes('too many') ||
    msg.includes('over_email_send_rate_limit')
  ) {
    return 'Too many requests. Try again in a few minutes.';
  }
  if (
    c.includes('email_address_invalid') ||
    msg.includes('email address') ||
    msg.includes('invalid email')
  ) {
    return 'Enter a valid email address.';
  }
  return "We couldn't reach Penrose. Check your connection and try again.";
}

export function PasswordReset() {
  const [searchParams] = useSearchParams();

  const redirectTo = searchParams.get('redirect_to');
  const next = searchParams.get('next');

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<ReactNode>(null);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setErrorText(null);

    const trimmed = email.trim();
    if (!trimmed) {
      setErrorText('Enter an email address.');
      return;
    }

    setSubmitting(true);
    try {
      // Anti-enumeration: we show the same success state regardless of
      // whether the email matches an account. Supabase's
      // resetPasswordForEmail already swallows the "no user" case server-side
      // and returns a generic non-error response, so the same posture holds
      // for any non-rate-limit response.
      const origin =
        typeof window !== 'undefined' ? window.location.origin : '';
      const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo: `${origin}${CONFIRM_PATH}`
      });
      if (error) {
        const errWithCode = error as { message?: string; code?: string; error_code?: string };
        const code = errWithCode.code ?? errWithCode.error_code;
        setErrorText(classifyResetError(error.message, code));
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
      setSubmitting(false);
    } catch {
      setErrorText(
        "We couldn't reach Penrose. Check your connection and try again."
      );
      setSubmitting(false);
    }
  }

  const carry = new URLSearchParams();
  if (redirectTo) carry.set('redirect_to', redirectTo);
  if (next) carry.set('next', next);
  const signInHref = carry.toString() ? `/sign-in?${carry.toString()}` : '/sign-in';

  return (
    <>
      <AuthChrome pageLabel="Reset password" />
      <main className="pr-main">
        <Card className="pr-card" aria-labelledby="pr-heading" data-pr-view={submitted ? 'submitted' : 'form'}>
          <h1 id="pr-heading" className="pr-heading">
            Reset your password
          </h1>

          {submitted ? (
            <>
              <Banner title="Check your inbox">
                If that email is registered, we've sent a reset link. The link is
                valid for 1 hour.
              </Banner>
              <p className="pr-alt">
                <Link to={signInHref}>Back to sign in</Link>
              </p>
            </>
          ) : (
            <>
              <p className="pr-lede">
                Enter the email associated with your account. We'll send a link
                to reset your password.
              </p>

              {errorText ? <Banner kind="error">{errorText}</Banner> : null}

              <form className="pr-form" onSubmit={handleSubmit} noValidate>
                <TextField
                  id="pr-email"
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={submitting}
                  className="pr-submit"
                >
                  {submitting ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>

              <p className="pr-alt">
                Remembered it? <Link to={signInHref}>Back to sign in</Link>
              </p>
            </>
          )}
        </Card>
      </main>
    </>
  );
}
